import React, { useRef, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { FixedSizeList as List } from "react-window";

// Constants
const LINE_HEIGHT = 38;
const MAX_LIST_HEIGHT = 500;
const SCROLL_THRESHOLD = 0.85; // Load more when 85% scrolled

// simple check if array is empty
const arrayIsEmpty = (arr) => !Array.isArray(arr) || !arr.length;

function isFocused({ props: { isFocused } }) {
	return isFocused === true;
}

function getCurrentIndex(children) {
    if (children.length !== undefined) {
	    return Math.max(children.findIndex(isFocused), 0);
    }
    return 0;
}

// react window MenuList component with infinite scroll support
// Pagination props are passed via selectProps to keep component reference stable
const MenuList = ({ 
	options, 
	children, 
	getValue,
	selectProps,
}) => {
	// Get pagination props from selectProps (passed via react-select)
	const { onLoadMore, hasMore, isLoadingMore } = selectProps?.paginationProps || {};
	
	const [value] = getValue();
	const listRef = useRef(null);
	const outerRef = useRef(null);
	
	// Track scroll position to preserve it when loading more items
	const scrollPositionRef = useRef(0);
	const prevChildrenLengthRef = useRef(children.length);
	const hasUserScrolledRef = useRef(false);
	
	// Calculate initial offset (only used on first render before user scrolls)
	const initialOffset = useMemo(
		() => Math.max(options.indexOf(value) * LINE_HEIGHT, 0),
		[options, value]
	);
	
	const maxHeight = useMemo(
		() => Math.min(children.length * LINE_HEIGHT, MAX_LIST_HEIGHT) || LINE_HEIGHT,
		[children.length]
	);
	
	const currentIndex = useMemo(
		() => getCurrentIndex(children),
		[children]
	);

	// Restore scroll position after new items are loaded
	useEffect(() => {
		const prevLength = prevChildrenLengthRef.current;
		const currentLength = children.length;
		
		// If items were added (pagination), restore scroll position
		if (currentLength > prevLength && prevLength > 0 && hasUserScrolledRef.current) {
			// Use double RAF to ensure DOM is fully updated
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (listRef.current && scrollPositionRef.current > 0) {
						listRef.current.scrollTo(scrollPositionRef.current);
					}
				});
			});
		}
		
		prevChildrenLengthRef.current = currentLength;
	}, [children.length]);

	// Handle keyboard navigation
    useEffect(() => {
		if (currentIndex >= 0 && listRef.current !== null) {
			listRef.current.scrollToItem(currentIndex);
		}
	}, [currentIndex]);

	// Handle scroll for infinite loading
	const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }) => {
		// Always track scroll position
		scrollPositionRef.current = scrollOffset;
		
		// Mark that user has scrolled (for scroll restoration logic)
		if (!scrollUpdateWasRequested && scrollOffset > 0) {
			hasUserScrolledRef.current = true;
		}
		
		// Don't trigger load more on programmatic scrolls
		if (scrollUpdateWasRequested) return;
		
		// Check if we should load more
		if (!onLoadMore || !hasMore || isLoadingMore) return;
		
		const totalHeight = children.length * LINE_HEIGHT;
		const scrolledRatio = (scrollOffset + maxHeight) / totalHeight;
		
		if (scrolledRatio >= SCROLL_THRESHOLD) {
			onLoadMore();
		}
	}, [onLoadMore, hasMore, isLoadingMore, children.length, maxHeight]);

	// Memoize row renderer
	const Row = useCallback(({ index, style }) => (
		<div style={style} className="whitespace-nowrap pr-2">
			{children[index]}
		</div>
	), [children]);

	// if no options, render react-selects default message
	if (arrayIsEmpty(children)) {
		return <div>{children}</div>;
	}

	return (
		<div>
			<List
				height={maxHeight}
				itemCount={children.length}
				itemSize={LINE_HEIGHT}
				initialScrollOffset={hasUserScrolledRef.current ? 0 : initialOffset}
				ref={listRef}
				outerRef={outerRef}
				onScroll={handleScroll}
			>
				{Row}
			</List>
			{isLoadingMore && (
				<div className="text-center py-2 text-sm text-text-3">
					Loading more...
				</div>
			)}
		</div>
	);
};

MenuList.propTypes = {
	options: PropTypes.array.isRequired,
	children: PropTypes.any.isRequired,
	getValue: PropTypes.func.isRequired,
	selectProps: PropTypes.shape({
		paginationProps: PropTypes.shape({
			onLoadMore: PropTypes.func,
			hasMore: PropTypes.bool,
			isLoadingMore: PropTypes.bool,
		}),
	}),
};

export default MenuList;
